import { useState, useRef, useEffect, useCallback } from 'react';
import InputMask from 'react-input-mask';
import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { getCountryList, DEFAULT_COUNTRY } from './countries';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
  isValid,
} from 'date-fns';

export enum InputFieldSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

interface InputFieldProps {
  label?: string;
  type?: string;
  mask?: string;
  size?: InputFieldSize;
  fullWidth?: boolean;
  className?: string;
  maskPlaceholder?: string;
  name: string;
  value: string | number | readonly string[] | boolean | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isPassword?: boolean;
  disabled?: boolean;
  required?: boolean;
  options?: { value: string; label: string }[];
  autoFocus?: boolean;
  autoComplete?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  darkMode?: boolean;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  inputRef?: React.RefObject<HTMLInputElement>;
  /** Render field in locked/read-only mode to prevent browser autofill */
  protected?: boolean;
  /** Callback when user clicks Edit on a protected field */
  onProtectedEdit?: () => void;
  /** Callback when user clicks Cancel on a protected field */
  onProtectedCancel?: () => void;
}

// Lock icon component for protected fields
const LockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Calendar icon component
const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Year range used when no min/max is given — wide enough for dates of birth.
const DEFAULT_YEARS_BACK = 120;
const DEFAULT_YEARS_AHEAD = 10;

const parseBound = (bound?: string | number): Date | null => {
  if (bound === undefined || bound === null || bound === '') return null;
  const date = parseISO(String(bound));
  return isValid(date) ? date : null;
};

// Date Picker Component
const DatePickerField: React.FC<{
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  min?: string | number;
  max?: string | number;
  baseStyle: string;
  sizeStyle: string;
  disabledStyle: string;
}> = ({
  label,
  name,
  value,
  onChange,
  fullWidth = false,
  required = false,
  disabled = false,
  placeholder = 'Select date',
  min,
  max,
  baseStyle,
  sizeStyle,
  disabledStyle,
}) => {
  const minDate = parseBound(min);
  const maxDate = parseBound(max);
  const isOutOfRange = (date: Date) =>
    (!!minDate && date < minDate && !isSameDay(date, minDate)) ||
    (!!maxDate && date > maxDate && !isSameDay(date, maxDate));

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const parsed = value ? parseISO(value) : null;
    if (parsed && isValid(parsed)) return startOfMonth(parsed);
    // No value yet: open on today, clamped into the allowed range
    if (maxDate && maxDate < new Date()) return startOfMonth(maxDate);
    if (minDate && minDate > new Date()) return startOfMonth(minDate);
    return startOfMonth(new Date());
  });
  const containerRef = useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update current month when value changes externally
  useEffect(() => {
    if (value) {
      try {
        const date = parseISO(value);
        if (!isSameMonth(date, currentMonth)) {
          setCurrentMonth(startOfMonth(date));
        }
      } catch {
        // Invalid date, ignore
      }
    }
  }, [value]);

  const handlePrevMonth = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(prev => addMonths(prev, 1));
  }, []);

  const handleMonthChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = Number(e.target.value);
    setCurrentMonth(prev => new Date(prev.getFullYear(), month, 1));
  }, []);

  const handleYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = Number(e.target.value);
    setCurrentMonth(prev => new Date(year, prev.getMonth(), 1));
  }, []);

  const handleDateSelect = useCallback((date: Date) => {
    const formattedValue = format(date, 'yyyy-MM-dd');
    // Create a synthetic event to match the onChange signature
    const syntheticEvent = {
      target: { name, value: formattedValue },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    setIsOpen(false);
  }, [name, onChange]);

  const handleClear = useCallback(() => {
    const syntheticEvent = {
      target: { name, value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    setIsOpen(false);
  }, [name, onChange]);

  const selectedDate = value ? parseISO(value) : null;

  // Generate calendar days
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();

  // Newest first so recent years sit at the top of the list
  const lastYear = maxDate ? maxDate.getFullYear() : today.getFullYear() + DEFAULT_YEARS_AHEAD;
  const firstYear = minDate ? minDate.getFullYear() : today.getFullYear() - DEFAULT_YEARS_BACK;
  const years: number[] = [];
  for (let y = lastYear; y >= firstYear; y--) years.push(y);
  // Keep the displayed year selectable even if it falls outside the range
  if (!years.includes(currentMonth.getFullYear())) {
    years.push(currentMonth.getFullYear());
    years.sort((a, b) => b - a);
  }

  // Format display value
  let displayValue = '';
  if (value) {
    try {
      displayValue = format(parseISO(value), 'MMM d, yyyy');
    } catch {
      displayValue = value;
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className={`block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 ${disabled ? 'opacity-50 dark:opacity-40' : ''}`}>
          {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          name={name}
          value={displayValue}
          placeholder={placeholder}
          onClick={() => !disabled && setIsOpen(true)}
          readOnly
          disabled={disabled}
          className={`${fullWidth ? 'w-full' : ''} ${baseStyle} ${sizeStyle} ${disabled ? disabledStyle : 'cursor-pointer'} pr-10`}
        />
        <button
          type="button"
          aria-label="Open calendar"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          <CalendarIcon />
        </button>
      </div>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4"
          style={{ minWidth: '280px' }}
        >
          {/* Month navigation */}
          <div className="flex items-center justify-between gap-1 mb-4">
            <button
              type="button"
              aria-label="Previous month"
              onClick={handlePrevMonth}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            >
              <ChevronLeftIcon />
            </button>
            <div className="flex items-center gap-1">
              <select
                aria-label="Month"
                value={currentMonth.getMonth()}
                onChange={handleMonthChange}
                className="text-sm font-semibold rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 py-1 pl-2 pr-7 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {MONTH_NAMES.map((monthName, index) => (
                  <option key={monthName} value={index}>
                    {monthName}
                  </option>
                ))}
              </select>
              <select
                aria-label="Year"
                value={currentMonth.getFullYear()}
                onChange={handleYearChange}
                className="text-sm font-semibold rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 py-1 pl-2 pr-7 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              aria-label="Next month"
              onClick={handleNextMonth}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            >
              <ChevronRightIcon />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isToday = isSameDay(day, today);
              const isDayDisabled = isOutOfRange(day);

              return (
                <button
                  key={index}
                  type="button"
                  disabled={isDayDisabled}
                  onClick={() => handleDateSelect(day)}
                  className={`
                    p-2 text-sm rounded-md transition-colors
                    ${!isCurrentMonth || isDayDisabled ? 'text-gray-300 dark:text-gray-600' : 'text-gray-700 dark:text-gray-200'}
                    ${isDayDisabled ? 'cursor-not-allowed' : isSelected ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                    ${isToday && !isSelected ? 'border border-indigo-500 dark:border-indigo-400' : ''}
                  `}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
            <button
              type="button"
              disabled={isOutOfRange(today)}
              onClick={() => handleDateSelect(new Date())}
              className="flex-1 px-2 py-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
            >
              Today
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Phone field with international country selector. Captures any country's number
// and emits canonical E.164 (e.g. "+447712345678") via the standard onChange
// contract, so the value stored/sent is always routable by the backend.
// ---------------------------------------------------------------------------

// Country selector data (full worldwide list) is shared with the address
// country dropdown — see ./countries. Built lazily via getCountryList() so it
// is created only when a phone field actually renders.
const DEFAULT_PHONE_COUNTRY: CountryCode = DEFAULT_COUNTRY;

const PhoneField: React.FC<{
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  baseStyle: string;
  sizeStyle: string;
  disabledStyle: string;
}> = ({
  label,
  name,
  value,
  onChange,
  fullWidth = false,
  required = false,
  disabled = false,
  placeholder,
  autoFocus = false,
  baseStyle,
  sizeStyle,
  disabledStyle,
}) => {
  // Parse an incoming value as E.164, falling back to the default region for
  // legacy national-format numbers (e.g. "8761234567") that have no country code.
  const parseIncoming = (v: string) =>
    parsePhoneNumberFromString(v) || parsePhoneNumberFromString(v, DEFAULT_PHONE_COUNTRY);

  const initial = value ? parseIncoming(value) : undefined;
  const [country, setCountry] = useState<CountryCode>(
    initial?.country || DEFAULT_PHONE_COUNTRY
  );
  const [national, setNational] = useState<string>(() =>
    initial?.country ? new AsYouType(initial.country).input(initial.nationalNumber) : ''
  );

  // Sync from an externally-provided value (form load / reset) — E.164 or legacy
  // national-format.
  useEffect(() => {
    if (!value) return;
    const parsed = parseIncoming(value);
    if (parsed && parsed.country) {
      setCountry(parsed.country);
      setNational(new AsYouType(parsed.country).input(parsed.nationalNumber));
    }
  }, [value]);

  // Compute canonical E.164 from the selected country + typed national number
  // and notify the parent through the standard onChange contract.
  const emit = (countryCode: CountryCode, nationalText: string) => {
    const formatter = new AsYouType(countryCode);
    formatter.input(nationalText);
    const phone = formatter.getNumber();
    const digits = nationalText.replace(/\D/g, '').replace(/^0+/, '');
    const e164 =
      phone?.number || (digits ? `+${getCountryCallingCode(countryCode)}${digits}` : '');
    onChange({
      target: { name, value: e164 },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as CountryCode;
    setCountry(next);
    setNational(new AsYouType(next).input(national.replace(/\D/g, '')));
    emit(next, national);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    setNational(new AsYouType(country).input(typed));
    emit(country, typed);
  };

  const selectedCallingCode = getCountryCallingCode(country);

  return (
    <>
      <label
        className={`block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 ${
          disabled ? 'opacity-50 dark:opacity-40' : ''
        }`}
      >
        {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
      </label>
      <div className={`flex gap-2 ${fullWidth ? 'w-full' : ''}`}>
        <select
          data-testid="phone-country-select"
          aria-label="Country code"
          name={`${name}-country`}
          value={country}
          onChange={handleCountryChange}
          disabled={disabled}
          className={`${baseStyle} ${sizeStyle} ${disabled ? disabledStyle : ''} max-w-[9rem]`}
        >
          {getCountryList().map((opt) => (
            <option key={opt.code} value={opt.code} className="bg-white dark:bg-gray-700">
              {opt.name} (+{opt.callingCode})
            </option>
          ))}
        </select>
        <input
          data-testid="phone-national-input"
          type="tel"
          inputMode="tel"
          name={name}
          value={national}
          onChange={handleNationalChange}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`flex-1 ${baseStyle} ${sizeStyle} ${disabled ? disabledStyle : ''}`}
          placeholder={placeholder || `+${selectedCallingCode} 7123 456789`}
        />
      </div>
    </>
  );
};

const InputField: React.FC<InputFieldProps> = ({
  label,
  type = 'text',
  mask,
  size = InputFieldSize.Medium,
  fullWidth = false,
  className,
  maskPlaceholder,
  name,
  value,
  onChange,
  onKeyDown,
  placeholder,
  isPassword = false,
  disabled = false,
  required = false,
  options = [],
  autoFocus = false,
  autoComplete,
  min,
  max,
  step,
  darkMode = false,
  enterKeyHint,
  inputRef,
  protected: isProtected = false,
  onProtectedEdit,
  onProtectedCancel,
}) => {
  const [isProtectedEditing, setIsProtectedEditing] = useState(false);
  const baseStyle = `px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
    focus:outline-none focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400
    text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
    placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200 ${className || ''}`;

  const sizeStyles = {
    [InputFieldSize.Small]: 'text-xs',
    [InputFieldSize.Medium]: 'text-sm py-2',
    [InputFieldSize.Large]: 'text-lg py-2.5'
  };

  const disabledStyle = 'opacity-50 cursor-not-allowed dark:opacity-40 dark:bg-gray-800';

  return (
    <div>
      {type === 'checkbox' ? (
        <div className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={Boolean(value)}
            onChange={onChange}
            disabled={disabled}
            className={`${baseStyle} ${sizeStyles[size]} ${disabled ? disabledStyle : ''} h-4 w-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400 border-gray-300 dark:border-gray-600 rounded`}
          />
          <label className={`ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100 ${disabled ? 'opacity-50 dark:opacity-40' : ''}`}>
            {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
          </label>
        </div>
      ) : type === 'textarea' ? (
        <>
          {label && (
            <label className={`block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 ${disabled ? 'opacity-50 dark:opacity-40' : ''}`}>
              {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
            </label>
          )}
          <textarea
            name={name}
            value={value as string}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            rows={4}
            autoFocus={autoFocus}
            className={`${fullWidth ? 'w-full' : ''} ${baseStyle} ${sizeStyles[size]} ${disabled ? disabledStyle : ''}`}
          />
        </>
      ) : type === 'select' ? (
        <>
          <label className={`block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 ${disabled ? 'opacity-50 dark:opacity-40' : ''}`}>
            {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
          </label>
          <select
            value={value as string}
            name={name}
            onChange={onChange}
            disabled={disabled}
            autoFocus={autoFocus}
            className={`${fullWidth ? 'w-full' : ''} ${baseStyle} ${sizeStyles[size]} ${disabled ? disabledStyle : ''}`}
          >
            <option value="" className="bg-white dark:bg-gray-700">Choose an option</option>
            {options.map(option => (
              <option key={option.value} value={option.value} className="bg-white dark:bg-gray-700">
                {option.label}
              </option>
            ))}
          </select>
        </>
      ) : type === 'date' ? (
        <DatePickerField
          label={label}
          name={name}
          value={value as string}
          onChange={onChange as (e: React.ChangeEvent<HTMLInputElement>) => void}
          fullWidth={fullWidth}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          min={min}
          max={max}
          baseStyle={baseStyle}
          sizeStyle={sizeStyles[size]}
          disabledStyle={disabledStyle}
        />
      ) : type === 'phone' ? (
        <PhoneField
          label={label}
          name={name}
          value={(value ?? '').toString()}
          onChange={onChange}
          fullWidth={fullWidth}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          autoFocus={autoFocus}
          baseStyle={baseStyle}
          sizeStyle={sizeStyles[size]}
          disabledStyle={disabledStyle}
        />
      ) : (
        <>
          <label className={`block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 ${disabled ? 'opacity-50 dark:opacity-40' : ''}`}>
            {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
          </label>
          {isProtected && !isProtectedEditing ? (
            <div className="flex items-center gap-2">
              <div
                data-testid={`protected-display-${name}`}
                className={`flex items-center gap-2 flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                  bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm`}
              >
                <LockIcon />
                <span>{'••••••••'}</span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  data-testid={`protected-edit-${name}`}
                  onClick={() => {
                    setIsProtectedEditing(true);
                    onProtectedEdit?.();
                  }}
                  className="px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500
                    dark:hover:text-indigo-300 border border-indigo-300 dark:border-indigo-600 rounded-md
                    hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
          ) : mask ? (
            <InputMask
              mask={mask}
              name={name}
              maskPlaceholder={maskPlaceholder || '_'}
              value={value as string}
              onChange={onChange}
              disabled={disabled}
              autoFocus={autoFocus}
              className={`${fullWidth ? 'w-full' : ''} ${baseStyle} ${disabled ? disabledStyle : ''}`}
              placeholder={placeholder}
              alwaysShowMask={false}
            />
          ) : (
            <div className={isProtectedEditing ? 'flex items-center gap-2' : ''}>
              <input
                ref={inputRef}
                type={isPassword ? 'password' : type}
                value={value as string}
                name={name}
                onChange={onChange}
                onKeyDown={onKeyDown}
                disabled={disabled}
                placeholder={placeholder}
                autoFocus={isProtectedEditing || autoFocus}
                autoComplete={isProtectedEditing ? 'new-password' : autoComplete}
                min={min}
                max={max}
                step={step}
                enterKeyHint={enterKeyHint}
                className={`w-full ${baseStyle} ${sizeStyles[size]} ${disabled ? disabledStyle : ''}`}
              />
              {isProtectedEditing && (
                <button
                  type="button"
                  data-testid={`protected-cancel-${name}`}
                  onClick={() => {
                    setIsProtectedEditing(false);
                    onProtectedCancel?.();
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-500
                    dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md
                    hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InputField;
